import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GlobalSettings, GlobalSettingsService } from '@core/globalSettings.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { AuthenticationStatus } from './models/authentication-status';
import { CookieReadWriteService } from './cookie-read-write.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private globalSettingsService: GlobalSettingsService,
		private cookiesService: CookieReadWriteService
	) {
		this.authStatus$ = new BehaviorSubject<AuthenticationStatus>({ isAuthenticated: this.isAuthenticated() });
	}
	private midcAppId = 'midc';

	authStatus$: BehaviorSubject<AuthenticationStatus>;

	impersonate(user: ImpersonateUserCredentials): Observable<GlobalSettings> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		return this.http
			.post(
				this.eupRoutesService.logonAs.url,
				{ contactId: user.contactId, businessPartnerId: user.businessPartnerId, sessionId: user.sessionId },
				{ headers, observe: 'response' }
			)
			.pipe(
				map((res: any) => {
					if (res.hasOwnProperty('body') && Object.keys(res.body).length > 0) {
						this.authStatus$.next({ isAuthenticated: true });

						return res.body as GlobalSettings;
					} else {
						// in case we want to redirect the user to another route,
						// we need to get an error in order to make unsubscribe the login subscription
						return res.json();
					}
				})
			);
	}

	getDetails(): Observable<LoginDetails> {
		return this.http.get(this.eupRoutesService.getLoginData(), undefined, undefined, false);
	}

	isAuthenticated(): boolean {
		const settings = this.globalSettingsService.get();

		return !!settings && settings.companies[0].id === this.cookiesService.businessCookie?.cookie?.companyId;
	}

	invalidateSession(sessionId: string): Observable<boolean> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		return this.http.deleteWithoutCancel(this.invalidateSessionUrl(sessionId), { headers, observe: 'response' }).pipe(
			map((res: any) => {
				return res.body;
			}),
			catchError(() => {
				return of(false);
			})
		);
	}

	private invalidateSessionUrl = (sessionId: string) =>
		`${this.eupRoutesService.iTeroWebAuthApiUrl}session/invalidate?sessionId=${sessionId}&appId=${this.midcAppId}`;
}

export class UserCredentials {
	username: string;
	password: string;

	constructor(username: string, password: string) {
		this.username = username;
		this.password = password;
	}
}

export interface SessionInfo {
	sessionId: string;
	accessToken: string;
	sessionType: string;
	idToken: string;
}

export class ImpersonateUserCredentials {
	contactId: number;
	businessPartnerId: number;
	sessionId?: string;

	constructor(contactId: number, businessPartnerId: number, sessionId?: string) {
		this.businessPartnerId = businessPartnerId;
		this.contactId = contactId;
		this.sessionId = sessionId;
	}
}

export class LoginDetails {
	appVersion: string;
	bffVersion: string;
	databaseVersion: string;
	forgotPasswordUrl: string;
	dbInfo: string;
	region: string;
	regionsEndpoints: { [key: string]: string };
}
